import moment from 'moment';

const {round, trunc: truncate} = Math;

enum EMoonPhase {
  New = 0,
  WaxingCrescent,
  QuarterMoon,
  WaxingGibbous,
  Full,
  WaningGibbous,
  LastQuarter,
  WaningCrescent,
  COUNT = 8,
}

const moonIcons: [
  New: string,
  WaxingCrescent: string,
  QuarterMoon: string,
  WaxingGibbous: string,
  Full: string,
  WaningGibbous: string,
  LastQuarter: string,
  WaningCrescent: string,
] = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];

enum EMoonPhaseName {
  New = 'New',
  WaxingCrescent = 'Waxing Crescent',
  QuarterMoon = 'Quarter Moon',
  WaxingGibbous = 'Waxing Gibbous',
  Full = 'Full',
  WaningGibbous = 'Waning Gibbous',
  LastQuarter = 'Last Quarter',
  WaningCrescent = 'Waning Crescent',
  COUNT = 'COUNT',
}

const MOON_PHASE_NAMES: EMoonPhaseName[] = [
  // Look-up table.
  EMoonPhaseName.New,
  EMoonPhaseName.WaxingCrescent,
  EMoonPhaseName.QuarterMoon,
  EMoonPhaseName.WaxingGibbous,
  EMoonPhaseName.Full,
  EMoonPhaseName.WaningGibbous,
  EMoonPhaseName.LastQuarter,
  EMoonPhaseName.WaningCrescent,
  EMoonPhaseName.COUNT,
];

// Reference: http://individual.utoronto.ca/kalendis/lunar/#FALC
// Also known as a synodic month.
// An average synodic month takes 29 days, 12 hours, 44 minutes, 3 seconds.
const LUNAR_CYCLE = 29.5305882; // 29.53058770576

const DAYS_PER_YEAR = 365.25;
const DAYS_PER_MONTH = 30.6;

// Number of days since known new moon on `1900-01-01`.
const DAYS_SINCE_NEW_MOON_1900_01_01 = 694039.09;

interface IResult {
  name: EMoonPhaseName;
  phase: EMoonPhase;
  icon: string;
}

function moonPhaseAlt(date: Date = new Date()): IResult {
  // let year = date.getYear()
  const year: number = date.getFullYear();

  const month: number = date.getMonth() + 1;
  const day: number = date.getDate();
  return moonPhase(year, month, day);
}

// Ported from `http://www.voidware.com/moon_phase.htm`.
function moonPhase(year: number, month: number, day: number): IResult {
  if (month < 3) {
    year--;
    month += 12;
  }

  month += 1;

  let totalDaysElapsed: number =
    DAYS_PER_YEAR * year +
    DAYS_PER_MONTH * month +
    day -
    DAYS_SINCE_NEW_MOON_1900_01_01;

  totalDaysElapsed /= LUNAR_CYCLE; // Divide by the lunar cycle.

  let phase: number = truncate(totalDaysElapsed);

  /*
      Subtract integer part to leave fractional part of original
      `totalDaysElapsed`.
     */
  totalDaysElapsed -= phase;

  // Scale fraction from `0-8`.
  phase = round(totalDaysElapsed * 8);
  if (phase >= 8) {
    phase = 0; // `0` and `8` are the same so turn `8` into `0`.
  }

  if (phase >= EMoonPhase.COUNT || phase < EMoonPhase.New) {
    throw new Error(`Invalid moon phase: ${phase}`);
  }

  return {phase, name: MOON_PHASE_NAMES[phase], icon: moonIcons[phase]};
}

const getJulianDate = (date = new Date()) => {
  const time = date.getTime();
  const tzoffset = date.getTimezoneOffset();

  return time / 86400000 - tzoffset / 1440 + 2440587.5;
};

const LUNAR_MONTH = 29.530588853;
const getLunarAge = (date = new Date()) => {
  const percent = getLunarAgePercent(date);
  const age = percent * LUNAR_MONTH;
  return age;
};
const getLunarAgePercent = (date = new Date()) => {
  const pourcentage = normalize(
    (getJulianDate(date) - 2451550.1) / LUNAR_MONTH,
  );
  return pourcentage;
};

const normalize = (value) => {
  value = value - Math.floor(value);
  if (value < 0) {
    value = value + 1;
  }
  return value;
};

const checkCloserToThreshold = (
  date: Date,
  age: number,
  threshold: number,
  closer: EMoonPhase,
  smaller: EMoonPhase,
  greater: EMoonPhase,
) => {
  const ageMinus1 = getLunarAge(moment(date).subtract(1, 'day').toDate());
  const agePlus1 = getLunarAge(moment(date).add(1, 'day').toDate());
  if (
    Math.abs(threshold - age) < Math.abs(threshold - ageMinus1) &&
    Math.abs(threshold - age) < Math.abs(threshold - agePlus1)
  ) {
    return closer;
  } else {
    return age < threshold ? smaller : greater;
  }
};

const getLunarPhase = (date = new Date()) => {
  const age = getLunarAge(date);
  if (age < 1.84566) {
    return checkCloserToThreshold(
      date,
      age,
      0,
      EMoonPhase.New,
      EMoonPhase.WaxingCrescent,
      EMoonPhase.WaxingCrescent,
    );
  } else if (age < 5.53699) {
    return EMoonPhase.WaxingCrescent;
  } else if (age < 9.22831) {
    return checkCloserToThreshold(
      date,
      age,
      LUNAR_MONTH / 4,
      EMoonPhase.QuarterMoon,
      EMoonPhase.WaxingCrescent,
      EMoonPhase.WaxingGibbous,
    );
  } else if (age < 12.91963) {
    return EMoonPhase.WaxingGibbous;
  } else if (age < 16.61096) {
    return checkCloserToThreshold(
      date,
      age,
      LUNAR_MONTH / 2,
      EMoonPhase.Full,
      EMoonPhase.WaxingGibbous,
      EMoonPhase.WaningGibbous,
    );
  } else if (age < 20.30228) {
    return EMoonPhase.WaningGibbous;
  } else if (age < 23.99361) {
    return checkCloserToThreshold(
      date,
      age,
      (3 * LUNAR_MONTH) / 4,
      EMoonPhase.LastQuarter,
      EMoonPhase.WaningGibbous,
      EMoonPhase.WaningCrescent,
    );
  } else if (age < 27.68493) {
    return EMoonPhase.WaningCrescent;
  }
  return checkCloserToThreshold(
    date,
    age,
    LUNAR_MONTH,
    EMoonPhase.New,
    EMoonPhase.WaningCrescent,
    EMoonPhase.WaningCrescent,
  );
};

const getNextPhaseTimestamp = (date: Date, phase: EMoonPhase) => {
  const age = getLunarAge(date);
  let seconds = 0;
  if (phase === EMoonPhase.New) {
    seconds = (LUNAR_MONTH - age) * 24 * 3600;
  } else if (phase === EMoonPhase.Full) {
    if (age < LUNAR_MONTH / 2) {
      seconds = (LUNAR_MONTH / 2 - age) * 24 * 3600;
    } else {
      seconds = ((3 * LUNAR_MONTH) / 2 - age) * 24 * 3600;
    }
  }
  return moment(date).add(seconds, 'seconds').unix();
};

export {
  EMoonPhase,
  EMoonPhaseName,
  MOON_PHASE_NAMES,
  getLunarPhase,
  getNextPhaseTimestamp,
  moonIcons,
  moonPhase,
  moonPhaseAlt,
};
